/**
 * /learn/programs/:programId/days/:dayId — courses scheduled on a single
 * program day. Mirrors the trainer's preview day-detail layout.
 */

import React, { useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import { useImageUrl } from '../../services/uploads';
import { usePrograms } from '../../hooks/learner/usePrograms';
import LearnerCourseProgressBar from '../../components/learner/LearnerCourseProgressBar';
import StickyProgressRow from '../../components/learner/StickyProgressRow';
import type { ProgramWithProgress } from '../../components/learner/ProgramCard';
import { useProgramDays } from '../../hooks/learner/useProgramDays';
import type { ProgramDayCourse } from '../../types/program';

const ProgramDayDetailPage: React.FC = () => {
  const { programId, dayId } = useParams<{ programId: string; dayId: string }>();
  const { programs, isLoading: programsLoading } = usePrograms();
  const { data: days = [], isLoading: daysLoading } = useProgramDays(programId);

  const program = programs.find((p) => p.id === programId);
  const day = days.find((d) => d.id === dayId);

  // Hook must run unconditionally (before the early returns below).
  const sortedCourses = useMemo(
    () => (day?.courses ?? []).slice().sort((a, b) => a.order_index - b.order_index),
    [day?.courses]
  );

  if (programsLoading || daysLoading) {
    return (
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 py-8 space-y-6 animate-pulse">
        <div className="h-4 w-32 rounded bg-text-tertiary/10" />
        <div className="h-12 w-2/3 rounded bg-text-tertiary/10" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-[2/3] rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    );
  }

  if (!program || !programId) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't find that program.</p>
        <Link to="/learn/programs" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Programs
        </Link>
      </div>
    );
  }

  if (!day) {
    // Day was renamed/deleted/missing — bounce back up to the days grid.
    return <Navigate to={`/learn/programs/${programId}`} replace />;
  }

  return (
    <motion.div {...slideUp} className="max-w-[1080px] mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Back link + program progress — pinned, mirroring the course pages. */}
      <StickyProgressRow>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-x-8 px-1">
          <Link
            to={`/learn/programs/${programId}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to {program.name}
          </Link>
          {(program as ProgramWithProgress).progress && (
            <div className="flex-1 min-w-[220px]">
              <LearnerCourseProgressBar
                percent={(program as ProgramWithProgress).progress!.progress_percent ?? 0}
                modulesCompleted={(program as ProgramWithProgress).progress!.courses_completed ?? 0}
                modulesTotal={(program as ProgramWithProgress).progress!.courses_total ?? 0}
              />
            </div>
          )}
        </div>
      </StickyProgressRow>

      {/* Breadcrumb path — PageHeader style (hero header page) */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 px-1 sm:px-2">
        <Link
          to="/learn/dashboard"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Home
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <Link
          to="/learn/programs"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Programs
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <Link
          to={`/learn/programs/${programId}`}
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors truncate max-w-[240px]"
        >
          {program.name}
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <span className="body-small text-primary-600 dark:text-primary-350 font-medium truncate max-w-[240px]">
          {day.title || `Day ${day.day_number}`}
        </span>
      </nav>

      <header className="px-1 sm:px-2">
        <p className="text-sm font-bold text-primary-500/80 uppercase tracking-[0.18em] mb-1">
          Day {day.day_number} of {program.duration_days}
        </p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-text-primary leading-tight">
          {day.title || `Day ${day.day_number}`}
        </h1>
      </header>

      {sortedCourses.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">
          This day doesn't have any courses scheduled yet.
        </div>
      ) : (
        <PreviewTileGrid maxColumns={3}>
          {sortedCourses.map((dc, i) => (
            <CourseTileCell key={dc.id} dayCourse={dc} index={i} />
          ))}
        </PreviewTileGrid>
      )}
    </motion.div>
  );
};

export default ProgramDayDetailPage;

// ─── Course tile inside a day ───────────────────────────────────────────────

interface CourseTileCellProps {
  dayCourse: ProgramDayCourse;
  index: number;
}

function CourseTileCell({ dayCourse, index }: CourseTileCellProps) {
  const navigate = useNavigate();
  const [coverUrl] = useImageUrl(dayCourse.course?.image_url ?? null);

  if (!dayCourse.course) {
    return (
      <div className="aspect-[2/3] rounded-2xl border border-text-tertiary/15 bg-text-tertiary/5 flex items-center justify-center text-text-tertiary text-xs italic px-3 text-center">
        Course unavailable
      </div>
    );
  }
  // A course opens through the member's program enrollment — progress
  // accrues on the program run, never on standalone history. No stamped
  // enrollment yet (e.g. invited before materialization) → locked.
  const assignmentId = dayCourse.assignment_id;
  const state: 'open' | 'locked' = assignmentId ? 'open' : 'locked';
  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={dayCourse.course.title}
      state={state}
      lockedScope="course"
      // A missing enrolment is not a sequencing lock — say which it is.
      lockedReason={assignmentId ? 'sequence' : 'no-access'}
      entranceDelay={index * 0.05}
      onClick={() => assignmentId && navigate(`/learn/courses/${assignmentId}`)}
    />
  );
}
